import React, { useCallback, useEffect, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';

import './seat-request-modal.css';


// CC_REQUEST_A_SEAT (2026-06-08): the owner-side prompt for the
// PRIVATE-game recruitment flow. An observer of a private game taps a
// recruitable AI seat and requests it; the backend targets the OWNER's
// channel with `SeatRequested` (point-to-point, like PeerLeftMidGame),
// GameGroupHandler dispatches `seat-requested-event`, and this modal
// surfaces Yes/No. Yes → approveSeatRequest (the SAME takeover the public
// claim runs); No → declineSeatRequest (the requester keeps watching).
//
// The deliberate friction is the point (DISCORD_KNOWLEDGE §6.4): the
// owner can see the seat's situation (faction, snohe, mid-turn) before
// handing it over — the joiner inherits it as-is.
//
// Mirrors PeerLeftModal: globally mounted in App.js with no isGroupLeader
// prop (owner-ness resolved LIVE at event time), a queue so rapid
// requests don't clobber, and a void listener that drops a head whose
// seat is no longer claimable.
//
// COPY PLACEHOLDER — Michael owns every player-facing string here.
export default function SeatRequestModal({ app, isGroupLeader }) {
    const [pendingRequests, setPendingRequests] = useState([]);
    const [voidNotice, setVoidNotice] = useState(null);
    const request = pendingRequests[0] || null;

    const propOwnerProvided = typeof isGroupLeader === 'boolean';

    // CC_DISCONNECT_VISIBILITY lesson (mirrored from PeerLeftModal):
    // resolve owner-ness LIVE at event time, never frozen at mount — the
    // listener is always bound, the owner check runs when the event fires
    // (well after identity settles).
    const isOwnerNow = useCallback(() => {
        if (propOwnerProvided) return isGroupLeader;
        const groupHandler = (
            app && app.game && app.game.groupHandler) || null;
        return Boolean(
            groupHandler
            && typeof groupHandler.isGroupLeader === 'function'
            && groupHandler.isGroupLeader());
    }, [app, isGroupLeader, propOwnerProvided]);

    useEffect(() => {
        const onRequested = (event) => {
            if (!isOwnerNow()) return;
            const detail = (event && event.detail) || {};
            if (detail.user_id === undefined || detail.user_id === null) {
                return;
            }
            setPendingRequests((prev) => [...prev, {
                userId: detail.user_id,
                displayName: detail.display_name || 'Player',
                seat: detail.seat,
                faction: detail.faction || '',
                snohe: detail.snohe,
                midTurn: Boolean(detail.mid_turn),
            }]);
        };
        // The approve could not be honored (requester gone / seat filled).
        // Drop the head and surface the owner's "no longer available"
        // notice.
        const onVoided = (event) => {
            const detail = (event && event.detail) || {};
            setVoidNotice(detail.message || 'That request is no longer valid.');
            setPendingRequests((prev) => prev.slice(1));
        };
        window.addEventListener('seat-requested-event', onRequested);
        window.addEventListener('seat-request-voided-event', onVoided);
        return () => {
            window.removeEventListener('seat-requested-event', onRequested);
            window.removeEventListener('seat-request-voided-event', onVoided);
        };
    }, [isOwnerNow]);

    const dismiss = useCallback(
        () => setPendingRequests((prev) => prev.slice(1)), []);

    const onApprove = useCallback(() => {
        if (!app || !app.game || !app.game.gameApi) return;
        if (request === null) return;
        app.game.gameApi.approveSeatRequest(request.userId, request.seat);
        dismiss();
    }, [app, request, dismiss]);

    const onDecline = useCallback(() => {
        if (!app || !app.game || !app.game.gameApi) return;
        if (request === null) return;
        app.game.gameApi.declineSeatRequest(request.userId, request.seat);
        dismiss();
    }, [app, request, dismiss]);

    const onDismissVoid = useCallback(() => setVoidNotice(null), []);

    if (request === null) {
        if (voidNotice !== null) {
            return (
                <Modal
                    show
                    onHide={onDismissVoid}
                    centered
                    className="seat-request-modal"
                >
                    <Modal.Header>
                        <Modal.Title>Seat request</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p>{voidNotice}</p>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="seed" onClick={onDismissVoid}>
                            OK
                        </Button>
                    </Modal.Footer>
                </Modal>
            );
        }
        return null;
    }

    const seatLabel = typeof request.seat === 'number'
        ? `Seat ${request.seat + 1}`
        : 'a seat';
    // PLACEHOLDER COPY — Michael owns the final wording.
    const factionLine = request.faction
        ? `${request.faction}` : 'this faction';

    return (
        <Modal
            show
            onHide={dismiss}
            backdrop="static"
            keyboard={false}
            centered
            className="seat-request-modal"
        >
            <Modal.Header>
                <Modal.Title>
                    {`${request.displayName} wants to join`}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p>
                    {`${request.displayName} is asking to take over ${seatLabel}`}
                    {` (${factionLine}).`}
                </p>
                <ul className="seat-request-modal-explainer">
                    <li>
                        <strong>Snohe</strong>
                        {`: ${request.snohe != null ? request.snohe : '—'}`}
                    </li>
                    {request.midTurn && (
                        <li>
                            This seat is mid-action — they inherit it as-is.
                        </li>
                    )}
                    <li>
                        <strong>Yes</strong> — they take the seat with its
                        full state. <strong>No</strong> — they keep watching.
                    </li>
                </ul>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="hex" onClick={onApprove}>
                    Yes, let them in
                </Button>
                <Button variant="seed" onClick={onDecline}>
                    No
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
